import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const BTN = { hu: "Elfogadom", en: "I accept", de: "Ich akzeptiere", fr: "J'accepte", cs: "Přijímám" };
const cookieName = (id) => `dsa_notice_${id}`;
const readCookie = (n) => document.cookie.split("; ").find((c) => c.startsWith(`${n}=`))?.split("=")[1];

export function MandatoryNotice() {
  const { pathname } = useLocation();
  const { lang } = useLang();
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const isPublic = !pathname.startsWith("/moderator") && !["/privacy", "/terms"].includes(pathname);

  useEffect(() => {
    if (!isPublic) return;
    api.get("/notice").then((r) => {
      const n = r.data.notice;
      setNotice(n && decodeURIComponent(readCookie(cookieName(n.id)) || "") !== n.version ? n : null);
    }).catch(() => {});
  }, [isPublic, pathname]);

  useEffect(() => {
    if (!notice || !isPublic) return undefined;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [notice, isPublic]);

  if (!notice || !isPublic) return null;
  const pick = (o) => (o?.[lang] || o?.en || "");

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/notice/accept", { id: notice.id, version: notice.version });
    } catch { /* still store locally */ }
    document.cookie = `${cookieName(notice.id)}=${encodeURIComponent(notice.version)}; max-age=31536000; path=/; SameSite=Lax`;
    setNotice(null);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/85 p-4 backdrop-blur-md" data-testid="mandatory-notice" role="dialog" aria-modal="true">
      <div className="flex max-h-[90vh] w-full max-w-xl animate-in fade-in zoom-in-95 flex-col rounded-2xl border border-zinc-800 bg-[#111113] text-zinc-100 shadow-2xl duration-300">
        <div className="flex items-center gap-3 border-b border-zinc-800/70 p-6">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-blue-600/15 text-blue-400"><ShieldCheck size={20} /></div>
          <h2 className="text-xl font-bold tracking-tight" data-testid="mandatory-notice-title">{pick(notice.title)}</h2>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap p-6 text-[14px] leading-relaxed text-zinc-300" data-testid="mandatory-notice-body">{pick(notice.body)}</div>
        <div className="border-t border-zinc-800/70 p-4">
          <button data-testid="mandatory-notice-accept" onClick={accept} disabled={busy}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 text-[14px] font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-50">
            {busy && <Loader2 size={16} className="animate-spin" />} {BTN[lang] || BTN.en}
          </button>
        </div>
      </div>
    </div>
  );
}
